/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useState } from 'react';
import { Form } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FormSubmitButton } from '@/components/buttons/form-submit-button';
import { routes } from '@/route-paths';
import type { ValidationMethod } from '@/extensions/priceshoes/lib/auth/validation-method';
import {
    type SecurityQuestion,
    getAnswerFieldName,
    readSecurityAnswers,
} from '@/extensions/priceshoes/lib/auth/security-questions';

interface SecurityQuestionsFormProps {
    /** Partner ID or email of the shopper validating their identity. */
    identifier: string;
    /** Validation methods of the shopper, kept to go back to the method selection. */
    validations: ValidationMethod[];
    /** Identity validation in progress, encrypted by the server; posted back as is. */
    identityFlow: string;
    /** Questions and possible answers from Auth Extension. */
    questions: SecurityQuestion[];
    /** Error resolved by the login action. */
    error?: string;
    returnUrl?: string | null;
}

/**
 * Security questions screen (PRIC-168 CA9): the shopper picks one answer per question to validate
 * their identity. Wrong answers can be retried; once the attempts run out the login action goes back to
 * the method selection with the security questions disabled.
 */
export default function SecurityQuestionsForm({
    identifier,
    validations,
    identityFlow,
    questions,
    error,
    returnUrl,
}: SecurityQuestionsFormProps): ReactElement {
    const { t } = useTranslation('extPriceshoes');
    const [unanswered, setUnanswered] = useState(false);

    // Require an answer per question before submitting; the action validates again.
    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        const complete = readSecurityAnswers(new FormData(event.currentTarget), questions) !== null;
        setUnanswered(!complete);
        if (!complete) {
            event.preventDefault();
        }
    };

    const formError = unanswered ? t('login.questions.errors.unanswered') : error;

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <h1 className="text-2xl font-semibold text-foreground">{t('login.questions.title')}</h1>
                <p className="text-foreground">{t('login.questions.description')}</p>
            </div>

            <Form method="post" className="space-y-6" noValidate onSubmit={handleSubmit}>
                {questions.map((question) => (
                    <fieldset key={question.id} className="space-y-3">
                        <legend className="font-semibold text-foreground">{question.label}</legend>
                        <RadioGroup name={getAnswerFieldName(question.id)} onValueChange={() => setUnanswered(false)}>
                            {question.options.map((option) => {
                                const optionId = `question-${question.id}-${option.id}`;
                                return (
                                    <div key={option.id} className="flex items-center gap-3">
                                        <RadioGroupItem id={optionId} value={option.id} />
                                        <Label htmlFor={optionId} className="font-normal cursor-pointer">
                                            {option.value}
                                        </Label>
                                    </div>
                                );
                            })}
                        </RadioGroup>
                    </fieldset>
                ))}

                {formError ? (
                    <p role="alert" className="text-sm text-destructive">
                        {formError}
                    </p>
                ) : null}

                {/* Posted back so the screen can be shown again after a wrong answer. */}
                <input type="hidden" name="identifier" value={identifier} />
                <input type="hidden" name="validations" value={JSON.stringify(validations)} />
                <input type="hidden" name="questions" value={JSON.stringify(questions)} />
                <input type="hidden" name="identityFlow" value={identityFlow} />
                <input type="hidden" name="loginMode" value="priceshoesSecurityAnswers" />
                {returnUrl ? <input type="hidden" name="returnUrl" value={returnUrl} /> : null}

                <FormSubmitButton defaultText={t('login.next')} submittingText={t('login.submitting')} />
            </Form>

            <div className="space-y-4 border-t border-border pt-6">
                {/* Identifies the shopper again, which shows the method selection with fresh methods. */}
                <Form method="post">
                    <input type="hidden" name="identifier" value={identifier} />
                    <input type="hidden" name="loginMode" value="identify" />
                    {returnUrl ? <input type="hidden" name="returnUrl" value={returnUrl} /> : null}
                    <Button type="submit" variant="outline" className="w-full">
                        {t('login.verification.otherMethod')}
                    </Button>
                </Form>
                <Button asChild variant="outline" className="w-full">
                    <Link to={routes.login}>{t('login.password.otherAccount')}</Link>
                </Button>
            </div>
        </div>
    );
}
